import type { MessageDescriptor } from '@tov-plus/domain';
import { componentCatalogs, msgForComponent } from './catalogs';
import { getLocale } from './i18n';

function resolveSingleDescriptor(
  descriptor: Pick<MessageDescriptor, 'key' | 'values'>,
): string | undefined {
  if (
    !descriptor ||
    typeof descriptor.key !== 'string' ||
    !descriptor.key.startsWith('tov.api.')
  )
    return undefined;
  const source = componentCatalogs.common.en[descriptor.key];
  if (!source?.trim()) return undefined;
  const values = descriptor.values ?? {};
  if (typeof values !== 'object' || Array.isArray(values)) return undefined;
  const placeholders = [...source.matchAll(/\{(\w+)\}/g)];
  if (
    placeholders.some(([, key]) => {
      const value = values[key!];
      return (
        !Object.hasOwn(values, key!) ||
        (typeof value !== 'string' && typeof value !== 'number') ||
        (typeof value === 'number' && !Number.isFinite(value))
      );
    })
  )
    return undefined;
  return msgForComponent(descriptor.key, getLocale(), 'common').replace(
    /\{(\w+)\}/g,
    (placeholder, key: string) =>
      Object.hasOwn(values, key) ? String(values[key]) : placeholder,
  );
}

export function resolveApiErrorMessage(document: {
  error: { message_descriptor?: MessageDescriptor };
}): string | undefined {
  const descriptor = document.error.message_descriptor;
  if (!descriptor) return undefined;
  if (descriptor.parts === undefined)
    return resolveSingleDescriptor(descriptor);
  if (
    descriptor.key !== 'tov.api.message_group' ||
    !Array.isArray(descriptor.parts) ||
    descriptor.parts.length === 0 ||
    descriptor.parts.length > 20
  )
    return undefined;
  const messages: string[] = [];
  for (const part of descriptor.parts) {
    if (
      !part ||
      typeof part !== 'object' ||
      Array.isArray(part) ||
      'parts' in part
    )
      return undefined;
    const message = resolveSingleDescriptor(part);
    if (message === undefined) return undefined;
    messages.push(message);
  }
  return resolveSingleDescriptor({
    key: descriptor.key,
    values: { messages: messages.join(' ') },
  });
}
